import React from 'react';
import { PickSelect } from './controls';
import { fmtTime, fromMinutes, toMinutes } from '../state/format';
import { STEP_MINUTES } from '../../constants';

const e = React.createElement;

const minutesOf = (shot) => shot.minutes || shot.duration_minutes || 0;

/**
 * Every half hour the shot could run in without leaving its event. The event
 * may close after midnight, so the end is carried past the day rather than
 * read as earlier than the start.
 */
const slotsInside = (shot) => {
  const window = shot.window || {};
  const from = toMinutes(window.start);
  let to = toMinutes(window.end);
  if (from === null || to === null) return [];
  if (to <= from) to += 1440;
  const out = [];
  for (let m = from; m + minutesOf(shot) <= to; m += STEP_MINUTES) out.push(m % 1440);
  return out;
};

/**
 * The couple shot, attached to the event it belongs to. It has three places to
 * be, the start of that event, the end of it, or a half hour inside it, and it
 * follows the event wherever the day is reordered, so it is never a card in
 * the list of its own. Inside the event it carries a time of its own; at
 * either end it takes the event's.
 */
const CoupleShotCard = ({ entry, locked, onMove, onPick, inside, canMove,
  timed }) => {
  const shot = entry.couple_shot;
  if (!shot) return null;
  const at = shot.position === 'end' ? 'end' : shot.position === 'during' ? 'during' : 'start';
  const minutes = minutesOf(shot);
  const name = entry.name || 'this event';

  // The places it can sit, named as places rather than as moves, so the
  // list reads back where the shot is now as well as offering the others.
  // Inside the event only on the ceremony itself: every other major event
  // gives the half hour up at one of its ends.
  // Before and after are offered only where the booking has room for them:
  // half an hour before its first event, or after its last, is time the
  // package never sold.
  const places = [
    ...(shot.can_sit_before === false ? [] : [['start', `Before ${name}`]]),
    ...(shot.can_sit_inside ? [['during', `In between ${name}`]] : []),
    ...(shot.can_sit_after === false ? [] : [['end', `After ${name}`]]),
  ];

  const slots = at === 'during' && !locked ? slotsInside(shot) : [];

  // Taken inside its event, the shot is a card of its own under that event
  // with its own hours, because it is a half hour the couple places rather
  // than one that hangs off an end and follows. Only where the day is being
  // timed: while it is being ordered there are no hours to place it in, so
  // it stays the row it was.
  if (at === 'during' && timed) {
    const latest = slots.length ? slots[slots.length - 1] : null;
    return e('div', { className: 'wd-card wd-shotcard' }, [
      e('div', { className: 'wd-thead', key: 'head' }, [
        e('span', { className: 'wd-nm', key: 'nm' },
          `${entry.name || 'Event'} ${(shot.label || 'couple shot').toLowerCase()}`),
        minutes
          ? e('span', { className: 'wd-badge', key: 'm' }, `${minutes} MIN`)
          : null,
      ]),
      e('div', { className: 'wd-times', key: 'times' }, [
        e('div', { className: 'wd-fieldwrap', key: 'start' }, [
          e('span', { className: 'wd-flabel', key: 'l' }, 'Start time'),
          locked || !slots.length
            ? e('div', { className: 'wd-timelocked', key: 'v' },
                shot.start ? fmtTime(shot.start)
                  : `Set ${entry.name || 'the event'} first`)
            : e(
                'select',
                {
                  key: 'v',
                  className: 'wd-select',
                  value: shot.start || '',
                  'aria-label': `When does the ${name} couple shot run`,
                  onChange: (ev) => onPick(entry.id, ev.target.value),
                },
                [e('option', { value: '', key: 'none' }, 'Select a time')]
                  .concat(slots.map((m) => e(
                    'option',
                    { value: fromMinutes(m), key: m },
                    fmtTime(fromMinutes(m))
                  )))
              ),
        ]),
        // The end follows the length, so it is read back rather than asked.
        e('div', { className: 'wd-fieldwrap', key: 'end' }, [
          e('span', { className: 'wd-flabel', key: 'l' }, 'End time'),
          e('div', { className: 'wd-timelocked', key: 'v' },
            shot.end ? fmtTime(shot.end) : `${minutes} minutes later`),
        ]),
      ]),
      // Only the half hours that fit inside the event are offered, so the
      // couple can see what the event leaves room for.
      slots.length && !locked
        ? e('p', { className: 'wd-caption', key: 'hint' },
            `Anywhere inside ${entry.name || 'the event'}, up to `
            + `${fmtTime(fromMinutes(latest))}.`)
        : null,
      locked || !canMove
        ? null
        : e('div', { className: 'wd-shotmove', key: 'move' },
            e(PickSelect, {
              label: 'Where it sits',
              ariaLabel: `Where the ${name} couple shot sits`,
              value: at,
              options: places.map(([value, label]) => ({ value, label })),
              onChange: (value) => onMove(entry.id, value),
            })),
    ]);
  }

  return e(
    'div',
    {
      className: `wd-shot is-${at}${inside ? ' is-inside' : ''}`,
    },
    [
      e('span', { className: 'wd-shot-tie', key: 'tie', 'aria-hidden': 'true' }),
      e('span', { className: 'wd-shot-name', key: 'nm' }, [
        // Always named after its event, because a day can carry more than one
        // and "couple shot" alone says nothing about which.
        e(
          'span',
          { key: 't' },
          `${entry.name || 'Event'} ${(shot.label || 'couple shot').toLowerCase()}`
        ),
        minutes ? e('span', { className: 'wd-shot-min', key: 'm' }, `${minutes} min`) : null,
        // Its own hours. At either end they follow the event; inside it they
        // are what the couple picked, and blank until they pick.
        shot.start && shot.end
          ? e(
              'span',
              { className: 'wd-shot-when', key: 'w' },
              `${fmtTime(shot.start)} to ${fmtTime(shot.end)}`
            )
          : null,
      ]),
      locked || !canMove
        ? null
        : e('div', { className: 'wd-shot-move', key: 'move' },
            e(PickSelect, {
              ariaLabel: `Where the ${name} couple shot sits`,
              value: at,
              options: places.map(([value, label]) => ({ value, label })),
              onChange: (value) => onMove(entry.id, value),
            })),
    ]
  );
};

export default CoupleShotCard;
